// src/_API/ApiConsume.js

import axios from 'axios';
import { uriConstructor } from './_uriConstructor';
import './axiosConfig'; // Interceptors, baseURL, etc.
import { clearAuthStorage } from '../Helpers/authStorage';
import { traceWamNav } from '../_Utils/wamTraceNav';

export const ApiConsume = async (
  section = "",
  method = "GET",
  BODY = {},
  idData = null,
  TOKEN = "",
  isFormData = false,
  PARAMS = {},
  shouldRedirectOn401 = true,
  skipAuthRefresh = false,
  EXTRA_HEADERS = {}
) => {
  // Validación de sección
  if (!section) {
    console.error("Section is undefined or empty.");
    return {
      status: 400,
      isLoading: false,
      msg_error: "Section is undefined or invalid.",
      data: idData ? {} : [],
    };
  }

  // Construcción de la URI usando el constructor
  let URI;
  try {
    URI = uriConstructor(section, idData);
    console.log(`[ApiConsume] ${method.toUpperCase()} → ${URI}`);
  } catch (error) {
    console.error(error.message);
    return {
      status: 400,
      isLoading: false,
      msg_error: error.message,
      data: idData ? {} : [],
    };
  }

  let DATA_RES = {
    status: 0,
    isLoading: true,
    msg_success: "",
    msg_error: "",
    data: idData ? {} : [],
    isPeriodLocked: false,
  };

  // Configurar cabeceras
  const HEADER_config = {};
  const accessToken =
    TOKEN ||
    localStorage.getItem("USER_AUTH") ||
    sessionStorage.getItem("USER_AUTH");

  if (accessToken) {
    HEADER_config["Authorization"] = `Bearer ${accessToken}`;
  }

  try {
    let AXIOS_RES;

    // Config para axios (manejo de FormData vs JSON)
    const config = {
      headers: {
        ...HEADER_config,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        // Todo header custom nuevo en EXTRA_HEADERS (ej. Idempotency-Key) debe agregarse
        // también a Access-Control-Allow-Headers en nginx (deploy/nginx/sistema_consorcios.conf del backend).
        ...(EXTRA_HEADERS || {}),
      },
      params: PARAMS,
      skipAuthRefresh,
    };

    const methodUpper = method.toUpperCase();
    const headerKeys = Object.keys(config.headers || {});
    const debugRequest = {
      method: methodUpper,
      uri: URI,
      baseURL: config.baseURL || axios.defaults.baseURL,
      headerKeys,
      hasBody: ["POST", "PUT", "PATCH"].includes(methodUpper) && !!BODY,
      params: PARAMS && Object.keys(PARAMS).length ? PARAMS : undefined,
    };
    console.log("[ApiConsume] Request config", debugRequest);

    switch (methodUpper) {
      case "GET":
        AXIOS_RES = await axios.get(URI, config);
        break;
      case "POST":
        AXIOS_RES = await axios.post(URI, BODY, config);
        break;
      case "PUT":
        AXIOS_RES = await axios.put(URI, BODY, config);
        break;
      case "PATCH": // ✅ soporte PATCH
        AXIOS_RES = await axios.patch(URI, BODY, config);
        break;
      case "DELETE":
        // Si alguna vez necesitás body en DELETE, usar axios.request({ method:'DELETE', url:URI, data: BODY, ...config })
        AXIOS_RES = await axios.delete(URI, config); // incluye headers y params
        break;
      default:
        throw new Error(`Invalid HTTP Method: ${method}`);
    }

    if (AXIOS_RES && [200, 201, 204].includes(AXIOS_RES.status)) {
      const successMessages = {
        POST: "El recurso ha sido creado con éxito",
        PUT: "El recurso ha sido actualizado con éxito",
        PATCH: "El recurso ha sido actualizado con éxito",
        DELETE: "El recurso ha sido eliminado con éxito",
        GET: "El recurso ha sido obtenido con éxito",
      };

      // 204 No Content → devolvemos estructura vacía
      const payload =
        AXIOS_RES.status === 204
          ? (idData ? {} : [])
          : (AXIOS_RES.data ?? (idData ? {} : []));

      DATA_RES = {
        ...DATA_RES,
        isLoading: false,
        data: payload,
        msg_success: successMessages[methodUpper] || "Operación completada con éxito",
        status: AXIOS_RES.status,
        isPeriodLocked: false,
      };
    }
  } catch (error) {
    const status = error.response?.status;
    const raw = error.response?.data;

    // Mensaje de error amigable
    const isPeriodLocked = status === 423 && raw?.error?.code === 'period_locked';
    const errorMessage = isPeriodLocked
      ? raw?.error?.message || "El período está bloqueado para modificaciones."
      : (typeof raw === 'string' && raw) ||
        raw?.detail ||
        (raw ? JSON.stringify(raw) : null) ||
        error.message ||
        "Error desconocido";

    DATA_RES = {
      status: status || 500,
      isLoading: false,
      msg_success: "",
      msg_error: errorMessage,
      data: idData ? {} : [],
      isPeriodLocked,
    };

    if (status === 403) {
      const payload = raw && typeof raw === 'object' ? raw : null;
      if (
        payload?.code === 'ADDON_REQUIRED' &&
        payload?.addon_code === 'CONTAB_ADMIN' &&
        typeof window !== 'undefined'
      ) {
        traceWamNav('API_DISPATCH_ADDONS_FORBIDDEN', {
          method: method.toUpperCase(),
          uri: URI,
          status,
          payload,
          pathname: window.location.pathname,
          search: window.location.search,
          storedContext: window.localStorage.getItem('wam_context'),
        });
        window.dispatchEvent(new CustomEvent('addons:forbidden', { detail: payload }));
      }
    }

    console.error("[ApiConsume] Request error", {
      method: method.toUpperCase(),
      uri: URI,
      baseURL: axios.defaults.baseURL,
      status: status ?? null,
      isNetworkError: !error.response,
      message: error.message,
      responseData: raw,
    });

    // Manejo de redirección 401
    if (status === 401 && shouldRedirectOn401) {
      traceWamNav('API_401_REDIRECT_HOME', {
        method: method.toUpperCase(),
        uri: URI,
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        search: typeof window !== 'undefined' ? window.location.search : '',
        storedContext:
          typeof window !== 'undefined' ? window.localStorage.getItem('wam_context') : null,
      });
      console.error("Autenticación fallida o token expirado.");
      await clearAuthStorage();
      window.location.href = "/";
    }
  }

  return DATA_RES;
};
